import React from 'react';
import { cn } from 'shared/lib/cn';

export type WorkbenchProps = {
  nav: React.ReactNode;
  topBar: React.ReactNode;
  /** CONF-218: the quote headline (the chip row). Rendered inside the sticky header
   *  under the top bar, so it pins exactly when the bar pins and scrolls away with
   *  the bar while the bar wraps. */
  headline?: React.ReactNode;
  notices?: React.ReactNode;
  main: React.ReactNode;
  rail?: React.ReactNode;
  overlays?: React.ReactNode;
  /** The sidebar is held open (the layout service's `spinePinned`). */
  navPinned?: boolean;
};

/**
 * Layout only (guidelines §1 rule 1): a spine, a sticky top bar, a scrolling
 * workbench column and a sticky rail. Knows nothing about domain.
 *
 * The spine is 64 px of icons at rest. It widens to 240 px, showing each item's
 * label, in three cases:
 *   - hover, after 150 ms (so a pass across the left edge on the way to a field
 *     does not flap it open; closing is immediate);
 *   - keyboard focus inside it — `:has(:focus-visible)`, NOT `:focus-within`,
 *     which would hold it open after a mouse click because the clicked button
 *     keeps focus;
 *   - pinned.
 * Hover and focus widen it OVER the page: reflowing the form would move fields
 * out from under the cursor. Only the pin moves the page over, because only the
 * pin is permanent.
 *
 * The top bar is sticky only while the page column is at least 1260 px wide. Below
 * that the Nameplate wraps onto two or three rows, and a bar that tall pinned to
 * the top would cover the rail's head and much of a small window, so it scrolls
 * away with the page instead. The headline (CONF-218) is a band inside the same
 * header element, so it follows the bar's rule rather than carrying a second one.
 *
 * Breakpoints: the rail stacks under the workbench below 1080 px; the spine
 * becomes a bottom bar below 620 px, where none of the widening applies. The
 * left-rail layout is the unconditional base and the bottom bar the only
 * width-gated override (see Workbench.test.tsx for why there is no min-width
 * variant here).
 */
export function Workbench({ nav, topBar, headline, notices, main, rail, overlays, navPinned = false }: WorkbenchProps) {
  return (
    // Route marker asserted by src/test/appRoutes.test.jsx (Phase 0a); the placeholder shell carried it before.
    <div data-testid="v2-shell" className="min-h-screen bg-ground font-body text-ink">
      <nav
        aria-label="Configurator"
        data-pinned={navPinned ? 'true' : 'false'}
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-16 overflow-y-auto overflow-x-hidden border-r border-hairline/15 bg-surface [scrollbar-width:none]',
          'transition-[width] duration-150 ease-out',
          'hover:w-60 hover:shadow-2xl hover:delay-150',
          '[&:has(:focus-visible)]:w-60 [&:has(:focus-visible)]:shadow-2xl',
          // Pinned, it is part of the page, not floating over it: no shadow, hovered or not.
          'data-[pinned=true]:w-60 data-[pinned=true]:shadow-none data-[pinned=true]:hover:shadow-none',
          'max-[620px]:inset-x-0 max-[620px]:top-auto max-[620px]:bottom-0 max-[620px]:h-14 max-[620px]:w-auto max-[620px]:overflow-visible max-[620px]:border-r-0 max-[620px]:border-t max-[620px]:shadow-none',
          'max-[620px]:hover:w-auto max-[620px]:[&:has(:focus-visible)]:w-auto max-[620px]:data-[pinned=true]:w-auto',
        )}
      >
        {nav}
      </nav>
      {/* The page column is a size container: the top bar and the rail size themselves to
          its width (which the pinned sidebar changes), not to the window's. */}
      <div className={cn('transition-[padding] duration-150 ease-out [container-type:inline-size] max-[620px]:pb-14 max-[620px]:pl-0', navPinned ? 'pl-60' : 'pl-16')}>
        <header className="top-0 z-20 border-b border-hairline/15 bg-ground/90 backdrop-blur [@container(min-width:1260px)]:sticky">
          {topBar}
          {headline && <div className="border-t border-hairline/10 px-6 py-2">{headline}</div>}
        </header>
        {notices && <div className="px-6 pt-3 empty:hidden">{notices}</div>}
        <div className="grid gap-6 px-6 py-6 [grid-template-columns:minmax(0,1fr)] min-[1080px]:[grid-template-columns:minmax(0,1fr)_480px]">
          <main className="flex min-w-0 flex-col gap-4">{main}</main>
          {rail && <aside aria-label="Live summary" className="min-w-0">{rail}</aside>}
        </div>
      </div>
      {overlays}
    </div>
  );
}
